import { useCallback, useEffect, useRef, useState } from "react";
import { holdMic, stopStream } from "../lib/micGuard.js";

/**
 * useRecorder — one short recording from the microphone, as a Blob.
 *
 * The browser's own speech recognition is unreliable on phones (iOS Safari
 * needs Siri & Dictation on and stops after the first pause; iOS Chrome has
 * none), so a text field with a mic records instead and asks the server to
 * transcribe — the same path the voice interview takes (Oct 16).
 *
 *   start()  → opens the mic and begins recording (rejects if denied)
 *   stop()   → resolves with the Blob (type from the browser: webm, mp4, …)
 *   cancel() → stops and discards
 *   state    → "idle" | "recording"
 *
 * Recording is capped at MAX_MS; the caller gets the blob then too.
 */
const MAX_MS = 60 * 1000;

export function supportsRecording() {
  return typeof window !== "undefined" && !!window.MediaRecorder
      && !!navigator.mediaDevices?.getUserMedia;
}

export function useRecorder({ onAutoStop } = {}) {
  const [state, setState] = useState("idle");
  const streamRef = useRef(null);
  const recRef    = useRef(null);
  const chunksRef = useRef([]);
  const capRef    = useRef(null);
  const resolveRef = useRef(null);

  const releaseRef = useRef(null);
  const settleRef  = useRef(null);
  // teardown: the one place the mic is actually turned off. Idempotent. Any
  // stop() still waiting is settled with null so no caller hangs.
  const teardown = useCallback(() => {
    clearTimeout(capRef.current);
    clearTimeout(settleRef.current);
    const rec = recRef.current;
    recRef.current = null;
    if (rec && rec.state !== "inactive") { rec.onstop = null; try { rec.stop(); } catch { /* fine */ } }
    stopStream(streamRef.current);
    streamRef.current = null;
    releaseRef.current?.(); releaseRef.current = null;
    const pending = resolveRef.current; resolveRef.current = null;
    pending?.(null);
    setState("idle");
  }, []);

  const stop = useCallback(() => new Promise(resolve => {
    const rec = recRef.current;
    if (!rec || rec.state === "inactive") { teardown(); resolve(null); return; }
    resolveRef.current = resolve;
    const finish = () => {
      clearTimeout(settleRef.current);
      const type = rec.mimeType || "audio/webm";
      const blob = new Blob(chunksRef.current, { type });
      recRef.current = null;          // teardown must not stop() it again
      const done = resolveRef.current; resolveRef.current = null;
      teardown();
      done?.(blob);
    };
    rec.onstop = finish;
    try { rec.stop(); } catch { teardown(); return; }
    // The tracks stay open only until the recorder flushes its last chunk
    // (onstop, normally within milliseconds). If a browser never fires it,
    // the mic goes off anyway after 1.5 s and whatever was captured is used.
    settleRef.current = setTimeout(finish, 1500);
  }), [teardown]);

  const start = useCallback(async () => {
    if (!supportsRecording()) throw new Error("unsupported");
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    streamRef.current = stream;
    // Page goes to the background → the recording is discarded and the mic
    // released. Nothing is worth an open microphone the person can't see.
    releaseRef.current = holdMic(() => teardown());
    const rec = new MediaRecorder(stream);
    chunksRef.current = [];
    rec.ondataavailable = e => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
    recRef.current = rec;
    rec.start();
    setState("recording");
    clearTimeout(capRef.current);
    capRef.current = setTimeout(async () => { const b = await stop(); onAutoStop?.(b); }, MAX_MS);
  }, [stop, onAutoStop]);

  const cancel = useCallback(() => { teardown(); }, [teardown]);

  useEffect(() => cancel, [cancel]);

  return { state, start, stop, cancel };
}
